import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { RouteChangeStartHandler } from './types';

/**
 * Runs `handler` on a React Router 7 navigation, passing the new
 * browser-relative URL (pathname + search + hash). The initial mount is not
 * treated as a navigation.
 *
 * Note: with React Router 7 this fires once the location has changed. Code that
 * needs to *prevent* a navigation must use `useNavigationBlocker` (RR7
 * `useBlocker`) instead — the legacy throw-to-cancel pattern is no longer
 * supported.
 */
export const useRouteChangeStart = (
  handler: RouteChangeStartHandler,
  enabled = true,
): void => {
  const location = useLocation();
  const url = `${location.pathname}${location.search}${location.hash}`;

  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const previousUrl = useRef(url);

  useEffect(() => {
    if (!enabled || previousUrl.current === url) {
      return;
    }
    previousUrl.current = url;
    handlerRef.current(url);
  }, [url, enabled]);
};
